import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { Grid2, IconButton, InputAdornment, TextField } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useMemo, useState } from "react";
import { ModalTreeFilterControl, TreeNode } from "./dialogFilter";

type TreePickerControlProps = {
	onSelect: (
		value: number,
		description: string,
		findNode: any,
		showPath?: boolean,
		listAssetDrawings?: number[],
		multipleSelectNodes?: boolean,
		onHide?: (showAssetTree?: boolean) => void,
		showAssetTree?: boolean,
	) => void;
	isLoading?: boolean;
	setListAssetDrawings?: Function;
	onSuccess?: (success: boolean) => void;
	showClearButton?: boolean;
	showFullPath?: boolean;
	disabled?: boolean;
	allowSearch?: boolean;
	dataSource: TreeNode;
	mostUsedCount?: number | null;
	itemsWithParent?: String[];
	dialogMaxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
	[key: string]: any;
};

function findNameById(node: TreeNode, id: number): string | null {
	if (node?.id === id) {
		return node.name;
	}
	if (node?.children) {
		for (const child of node.children) {
			const result = findNameById(child, id);
			if (result !== null) {
				return result;
			}
		}
	}
	return null;
}

function getNodePath(
	node: TreeNode,
	nodeId: number | string,
	parentPath: string = "",
): string | null {
	const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name;

	if (node.id === nodeId) {
		return currentPath;
	}

	if (node.children && node.children.length > 0) {
		for (const child of node.children) {
			const foundPath = getNodePath(child, nodeId, currentPath);
			if (foundPath !== null) {
				return foundPath;
			}
		}
	}

	return null;
}

export const TreePickerControlV2 = (props: TreePickerControlProps) => {
	const {
		value,
		onSelect,

		styleLabel,
		dataSource,
		getOptionName,
		inputTitle = "Asset",
		showPath = false,
		multipleSelectNodes = false,
		showClearButton = false,
		showFullPath = false,
		showAssetTree,
		onHide,
		listAssetDrawings = [],
		onSuccess,
		isLoading,
		disabled = false,
		mostUsedCount = 0,
		itemsWithParent = [],
		dialogMaxWidth = "sm",
		allowSearch = false,
		...other
	}: TreePickerControlProps = props;

	const [open, setOpen] = useState(false);

	const handleClickListItem = (event: any) => {
		event.stopPropagation();
		if (disabled) return;
		setOpen(true);
	};

	const handleClose = (newValue: any, newDescription: any, findNode: any) => {
		setOpen(false);
		if (newValue !== undefined && newValue !== null) {
			if (onSelect !== undefined) {
				onSelect(newValue, newDescription, findNode);
			}
		}
	};

	const description = useMemo(
		() => findNameById(dataSource, value) || "",
		[dataSource, value],
	);

	const fullPath = useMemo(
		() => getNodePath(dataSource, value) || "",
		[dataSource, value],
	);

	return (
		<Grid2
			container
			sx={{
				width: "100%",
				backgroundColor: (theme) => theme.palette.background.paper,
			}}
		>
			{!showAssetTree ? (
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					style={{ position: "relative" }}
					title={props.description}
				>
					<Grid2 style={{ position: "relative", width: "100%" }}>
						<TextField
							id="treePickerTextField"
							label={inputTitle || ""}
							value={showFullPath ? fullPath : description}
							autoComplete="off"
							disabled
							slotProps={{
								input: {
									style: {
										color: disabled ? "grey" : "black",
										cursor: "pointer",
										paddingRight: 25,
									},
									endAdornment: (
										<InputAdornment
											position="end"
											style={{
												cursor: "pointer",
											}}
										>
											{showClearButton && value ? (
												<IconButton
													aria-label="clear input"
													onClick={(e) => {
														e.stopPropagation();
														onSelect(0, "", "");
													}}
													style={{
														cursor: "pointer",
														zIndex: 100,
													}}
													edge="end"
													size="small"
												>
													<ClearIcon />
												</IconButton>
											) : null}
											<AccountTreeIcon
												style={{
													cursor: "pointer",
												}}
											/>
										</InputAdornment>
									),
								} as any,
							}}
						/>
					</Grid2>
					<div
						style={{
							position: "absolute",
							top: 0,
							bottom: 0,
							left: 0,
							right: 0,
							cursor: "pointer",
						}}
						title={fullPath}
						onClick={handleClickListItem}
					></div>
				</Grid2>
			) : (
				<div></div>
			)}
			<Grid2 size={{ xs: 12, md: 12 }}>
				<ModalTreeFilterControl
					classes={{
						paper: {
							width: "100%",
							maxHeight: 600,
						},
					}}
					onHide={onHide}
					id="modal-treeview-filter"
					title={props.title}
					keepMounted
					open={open}
					onClose={handleClose}
					isLoading={isLoading}
					value={value}
					data={dataSource}
					onBackdropClick={() => setOpen(false)}
					multipleSelectNodes={multipleSelectNodes}
					listAssetDrawings={listAssetDrawings}
					setListAssetDrawings={listAssetDrawings}
					onSuccess={onSuccess}
					mostUsedCount={mostUsedCount}
					itemsWithParent={itemsWithParent}
					dialogMaxWidth={dialogMaxWidth}
					allowSearch={allowSearch}
					{...(other as any)}
				></ModalTreeFilterControl>
			</Grid2>
		</Grid2>
	);
};
